/**
 * Token-bound thesis card composite for demo overview panels.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { cva } from "class-variance-authority";
import { cn } from "../styling/index.js";
import { colorWithAlpha } from "./MonographPanelColor.js";
import type {
  MonographPanelCardProps,
  MonographPanelTokens,
  MonographProseProps,
  MonographPullQuoteProps,
  MonographSectionHeadingProps,
  MonographThesisCardProps,
} from "./MonographPanelPrimitiveTypes.js";

const thesisCardClass = cva("csk-monograph-thesis-card");

interface ThesisFactoryInput {
  readonly Card: (props: MonographPanelCardProps) => React.JSX.Element;
  readonly Prose: (props: MonographProseProps) => React.JSX.Element;
  readonly PullQuote: (props: MonographPullQuoteProps) => React.JSX.Element;
  readonly SectionHeading: (props: MonographSectionHeadingProps) => React.JSX.Element;
  readonly tokens: MonographPanelTokens;
}

export function createMonographPanelThesis({
  Card,
  Prose,
  PullQuote,
  SectionHeading,
  tokens,
}: ThesisFactoryInput): {
  readonly ThesisCard: (props: MonographThesisCardProps) => React.JSX.Element;
} {
  function ThesisCard({
    children,
    heading,
    headingStyle,
    quote,
    quoteSource,
    color = tokens.accent,
    glowColor,
    bodyVariant = "lead",
    bodyTone,
    bodyStyle,
    className,
    style,
  }: MonographThesisCardProps): React.JSX.Element {
    const hasQuote = quote !== undefined && quote !== null;
    const hasBody = children !== undefined && children !== null;
    const hasHeading = heading !== undefined && heading !== null;
    const resolvedGlowColor = glowColor ?? colorWithAlpha(color, "1a");

    return (
      <Card glowColor={resolvedGlowColor} {...(style !== undefined ? { style } : {})}>
        <div className={cn(thesisCardClass(), className)}>
          {hasHeading ? (
            <SectionHeading style={{ marginBottom: 12, ...headingStyle }}>{heading}</SectionHeading>
          ) : null}
          {hasQuote ? (
            <PullQuote color={color} source={quoteSource}>
              {quote}
            </PullQuote>
          ) : null}
          {hasBody ? (
            <Prose
              variant={bodyVariant}
              {...(bodyTone !== undefined ? { tone: bodyTone } : {})}
              style={{
                margin: hasQuote || hasHeading ? "16px 4px 0" : 0,
                ...bodyStyle,
              }}
            >
              {children}
            </Prose>
          ) : null}
        </div>
      </Card>
    );
  }

  return { ThesisCard };
}
